import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  Chip,
  Alert,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  CircularProgress,
  Skeleton,
  Fade,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  TableSortLabel
} from '@mui/material';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import TuneIcon from '@mui/icons-material/Tune';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useTranslation } from 'react-i18next';
import SensitivityBadge from './SensitivityBadge';
import { getParameterLookupId } from '../../services/catalogService';

const DATA_EXPLORATION_COPY = {
  de: {
    heroTitle: 'Verfügbarkeit & Nutzung',
    cases: 'Fälle',
    coverage: 'Abdeckung',
    stationGroups: 'Stationsgruppen',
    availableSince: 'Verfügbar seit',
    qualityProfile: 'Qualitätsprofil',
    quality: 'Qualität',
    activeFilter: 'Aktiver Filter:',
    followingViewsFiltered: 'Alle nachfolgenden Ansichten sind auf diesen Bereich gefiltert',
    filterEnabled: 'Filter aktiviert!',
    filterEnabledBody:
      'Die Sektionen Nutzungs-Charakteristik, Datenqualität und Ähnliche Parameter zeigen jetzt ausschließlich Daten für {{group}}. Klicken Sie auf das ×-Symbol im Filter-Chip, um zur Gesamtansicht zurückzukehren.',
    availabilityByArea: 'Datenfilter: Verfügbarkeit nach Bereich',
    privacyTooltip: 'Einzelne Stationen werden nur angezeigt, wenn mindestens 30 Fälle vorliegen (Datenschutz)',
    selectAreaPrompt:
      'Wählen Sie einen Bereich aus, um alle nachfolgenden Ansichten (Nutzungs-Charakteristik, Datenqualität, Ähnliche Parameter) auf diesen Bereich zu filtern.',
    area: 'Bereich',
    stations: 'Stationen',
    details: 'Details',
    allAreas: 'Alle Bereiche',
    noStationsMeetMinimum: 'Keine Stationen erfüllen das Mindestkriterium von 30 Fällen (Datenschutz)',
    stationsInGroup: 'Stationen in {{group}}',
    usageCharacteristics: 'Nutzungs-Charakteristik',
    basedOnGroup: 'Daten basieren auf: {{group}}',
    basedOnAll: 'Daten basieren auf allen Bereichen (aggregiert)',
    measurementFrequency: 'Messfrequenz',
    mostCommon: 'Häufigste',
    median: 'Median',
    spread: 'Streuung',
    intervalDistribution: 'Verteilung der Messintervalle',
    noData: 'Keine Daten verfügbar',
    measurementsPerCase: 'Messungen pro Fall',
    statsBasedOnCases: 'Statistik basiert auf {{count}} Fällen mit mindestens einer Messung',
    averageShort: 'Durchschn.',
    total: 'Gesamt',
    countDistribution: 'Verteilung nach Anzahl',
    continuousInfo: '{{percent}}% der Fälle haben ≥10 Messungen (kontinuierlich)',
    methodology:
      'Methodologie: Alle Statistiken basieren auf Fällen mit mindestens einer Messung ({{count}} Fälle). Messfrequenz-Statistiken beziehen sich auf {{group}}.',
    mainUsageArea: 'die Hauptnutzungsbereiche',
    dataQuality: 'Datenqualität & Validierung',
    completeness: 'Vollständigkeit',
    plausibility: 'Plausibilität',
    missing: 'Missing',
    outside: '{{percent}}% außerhalb',
    unitsInData: 'Einheiten in den Daten:',
    conversionRequired: 'Konversion erforderlich: kPa→mmHg (×7.5), cmH₂O→mmHg (÷1.36)',
    outlierHandling: 'Outlier-Behandlung',
    important: 'Wichtig:',
    outlierNotice: 'Extremwerte NICHT entfernt – klinisch relevant bei Sepsis, Schock & hypertensiven Krisen.',
    iqrMethod: 'IQR-Methode',
    physiological: 'Physiologisch',
    systemHistory: 'Systemhistorie',
    systemChange:
      'Systemwechsel 01/2020: Altsystem rundete auf 5er-Schritte. Varianzzunahme in 2018-2022 Analysen ist Messartefakt, keine klinische Änderung.',
    similarParameters: 'Parameter mit ähnlicher Erfassung',
    similarParametersTooltip: 'Parameter mit ähnlicher Messfrequenz, Stationsnutzung und Erfassungsmuster',
    similarParametersSummary:
      'Diese Parameter werden mit ähnlicher Häufigkeit und auf ähnlichen Stationen erfasst (unabhängig vom medizinischen Inhalt)',
    noSimilarParameters: 'Für diesen Filter sind noch keine ähnlichen Parameter hinterlegt.',
    similarityReasons: 'Ähnlichkeitsgründe',
    frequency: 'Frequenz',
    similarity: 'Ähnlichkeit',
    similarityNote:
      'Hinweis: Die Ähnlichkeit bezieht sich auf die Erfassungseigenschaften (Messfrequenz, Stationsnutzung, Erfassungsmuster), nicht auf die medizinische Bedeutung.',
    measurementCountCategories: {
      single: 'Einzelmessung (1)',
      few: 'Wenige (2-10)',
      medium: 'Mittel (11-50)',
      many: 'Viele (>50)',
    },
    similarFrequencyLabels: {
      hourly: 'Stündlich',
      continuous: 'Kontinuierlich',
      fourHourly: '4-stündlich',
      twelveToTwentyFour: '12-24h',
      irregular: 'Unregelmäßig',
      continuousBeatmet: 'Kontinuierlich',
      daily: 'Täglich',
      everyTwoToThreeDays: 'Alle 2-3 Tage',
      everyFourToSixHours: '4-6 Stunden',
      asNeeded: 'Bei Bedarf',
      unknown: 'Unbekannt',
    },
    measurementPatternInterpretations: {
      mostlyHourly:
        'Hauptsächlich stündliche Messungen auf ICU-Stationen, vereinzelt 4-12h Intervalle auf Normalstationen. Sehr zuverlässige und häufig erfasste Vitalparameter.',
      continuousIcuOr:
        'Kontinuierliche Überwachung auf Intensivstationen und OP, regelmäßige Messungen auf Normalstationen. Am häufigsten erfasster Vitalparameter.',
      continuousIcuImc:
        'Kontinuierliche Überwachung auf ICU und OP, regelmäßige Messungen auf IMC. Kritischer Vitalparameter mit hoher Priorität.',
      dailyLab:
        'Tägliche Routine-Laborwerte auf ICU/IMC, bei Bedarf häufiger. Auf Normalstationen weniger frequent.',
      renalMonitoring:
        'Regelmäßige Nierenfunktionskontrolle, besonders bei Risikopatienten. Häufiger bei eingeschränkter Nierenfunktion.',
      ventilatedIcu:
        'Nur bei beatmeten Patienten auf Intensivstationen. Bei diesen Fällen kontinuierliche Erfassung mit sehr hoher Frequenz. Sehr spezifischer Parameter.',
      temperatureRoutine:
        'Regelmäßige Temperaturkontrolle auf allen Stationen. Häufiger bei fieberhaften Infektionen oder postoperativ.',
      sepsisMarker:
        'Wichtiger Sepsis- und Schock-Marker. Häufige Messungen bei kritisch kranken Patienten, seltener auf Normalstationen.',
      noUsageData:
        'Neu hinzugefügter Parameter, noch keine Nutzungsdaten verfügbar.',
    },
    similarityReasonLabels: {
      sameMeasurementFrequency: 'Gleiche Messfrequenz',
      similarStationUse: 'Ähnliche Stationsnutzung',
      continuousCapture: 'Kontinuierliche Erfassung',
      vitalSign: 'Vitalparameter',
      relatedMeasurement: 'Zusammengehörige Messung',
      regularCapture: 'Regelmäßige Erfassung',
      identicalStationUse: 'Identische Stationsnutzung',
      identicalIcuStations: 'Identische ICU-Stationen',
      sameFrequency: 'Gleiche Frequenz',
      identicalMonitoringSystem: 'Identisches Monitoring-System',
      icuVitalSign: 'ICU-Vitalparameter',
      icuStandard: 'ICU-Standard',
      imcVitalSign: 'IMC-Vitalparameter',
      imcStandard: 'IMC-Standard',
      identicalImcStations: 'Identische IMC-Stationen',
      basicVitalSign: 'Basisvitalparameter',
      stationBreadth: 'Stationsbreite',
      generalWardRoutine: 'Normalstations-Routine',
      intraOpStandard: 'Intraop-Standard',
      identicalOperatingRooms: 'Identische OP-Säle',
      anesthesiaRequired: 'Anästhesie-Pflicht',
      labValue: 'Laborwert',
      similarSamplingFrequency: 'Ähnliche Abnahmefrequenz',
      electrolytePanel: 'Elektrolyt-Panel',
    },
    stationGroupLabels: {
      icu: 'Intensivstationen',
      imc: 'IMC (Intermediate Care)',
      ward: 'Normalstationen',
      opDiagnostics: 'OP & Diagnostik',
      lab: 'Labor',
      other: 'Sonstige',
    },
    radar: {
      caseCoverage: 'Fallabdeckung',
      completeness: 'Vollständigkeit',
      availability: 'Verfügbarkeit',
      stationBreadth: 'Stationsbreite',
      continuity: 'Kontinuität',
      caseCoverageDescription: 'Prozentsatz der Fälle mit mindestens einer Messung',
      completenessDescription: 'Anteil vollständiger Messungen ohne fehlende Werte',
      availabilityDescription: 'Seit {{years}} Jahren verfügbar (max. 10 Jahre = 100%)',
      stationBreadthDescription: 'Auf {{count}} Stationsgruppen verfügbar (6+ = 100%)',
      continuityDescription: 'Prozentsatz der Fälle mit ≥10 Messungen (kontinuierliche Erfassung)',
    },
  },
  en: {
    heroTitle: 'Availability & usage',
    cases: 'Cases',
    coverage: 'Coverage',
    stationGroups: 'Station groups',
    availableSince: 'Available since',
    qualityProfile: 'Quality profile',
    quality: 'Quality',
    activeFilter: 'Active filter:',
    followingViewsFiltered: 'All following views are filtered to this area',
    filterEnabled: 'Filter enabled!',
    filterEnabledBody:
      'The Usage characteristics, Data quality, and Similar parameters sections now show data only for {{group}}. Click the × icon in the filter chip to return to the overall view.',
    availabilityByArea: 'Data filter: availability by area',
    privacyTooltip: 'Individual stations are shown only when at least 30 cases are available (privacy protection)',
    selectAreaPrompt:
      'Select an area to filter all following views (usage characteristics, data quality, similar parameters) to that area.',
    area: 'Area',
    stations: 'Stations',
    details: 'Details',
    allAreas: 'All areas',
    noStationsMeetMinimum: 'No stations meet the minimum threshold of 30 cases (privacy protection)',
    stationsInGroup: 'Stations in {{group}}',
    usageCharacteristics: 'Usage characteristics',
    basedOnGroup: 'Data is based on: {{group}}',
    basedOnAll: 'Data is based on all areas (aggregated)',
    measurementFrequency: 'Measurement frequency',
    mostCommon: 'Most common',
    median: 'Median',
    spread: 'Spread',
    intervalDistribution: 'Distribution of measurement intervals',
    noData: 'No data available',
    measurementsPerCase: 'Measurements per case',
    statsBasedOnCases: 'Statistics based on {{count}} cases with at least one measurement',
    averageShort: 'Avg.',
    total: 'Total',
    countDistribution: 'Distribution by count',
    continuousInfo: '{{percent}}% of cases have ≥10 measurements (continuous)',
    methodology:
      'Methodology: All statistics are based on cases with at least one measurement ({{count}} cases). Measurement-frequency statistics refer to {{group}}.',
    mainUsageArea: 'the main usage areas',
    dataQuality: 'Data quality & validation',
    completeness: 'Completeness',
    plausibility: 'Plausibility',
    missing: 'Missing',
    outside: '{{percent}}% outside range',
    unitsInData: 'Units in the data:',
    conversionRequired: 'Conversion required: kPa→mmHg (×7.5), cmH₂O→mmHg (÷1.36)',
    outlierHandling: 'Outlier handling',
    important: 'Important:',
    outlierNotice: 'Extreme values are NOT removed – clinically relevant for sepsis, shock, and hypertensive crises.',
    iqrMethod: 'IQR method',
    physiological: 'Physiological',
    systemHistory: 'System history',
    systemChange:
      'System change 01/2020: the legacy system rounded to steps of 5. The higher variance in 2018-2022 analyses is a measurement artifact, not a clinical change.',
    similarParameters: 'Parameters with similar capture behavior',
    similarParametersTooltip: 'Parameters with similar measurement frequency, station usage, and capture pattern',
    similarParametersSummary:
      'These parameters are captured with similar frequency and in similar stations (independent of medical meaning)',
    noSimilarParameters: 'No similar parameters are stored for this filter yet.',
    similarityReasons: 'Similarity reasons',
    frequency: 'Frequency',
    similarity: 'Similarity',
    similarityNote:
      'Note: similarity refers to capture characteristics (measurement frequency, station usage, capture pattern), not medical meaning.',
    measurementCountCategories: {
      single: 'Single measurement (1)',
      few: 'Few (2-10)',
      medium: 'Medium (11-50)',
      many: 'Many (>50)',
    },
    similarFrequencyLabels: {
      hourly: 'Hourly',
      continuous: 'Continuous',
      fourHourly: 'Every 4 hours',
      twelveToTwentyFour: '12-24h',
      irregular: 'Irregular',
      continuousBeatmet: 'Continuous (ventilated)',
      daily: 'Daily',
      everyTwoToThreeDays: 'Every 2-3 days',
      everyFourToSixHours: 'Every 4-6 hours',
      asNeeded: 'As needed',
      unknown: 'Unknown',
    },
    measurementPatternInterpretations: {
      mostlyHourly:
        'Mostly hourly measurements in ICU units, with occasional 4-12h intervals on general wards. Very reliable and frequently captured vital sign.',
      continuousIcuOr:
        'Continuous monitoring in intensive care units and the operating room, with regular measurements on general wards. The most frequently captured vital sign.',
      continuousIcuImc:
        'Continuous monitoring in ICU and the operating room, with regular measurements in IMC. Critical vital sign with high priority.',
      dailyLab:
        'Daily routine lab values in ICU/IMC, more frequent when needed. Less frequent on general wards.',
      renalMonitoring:
        'Regular kidney function monitoring, especially for at-risk patients. More frequent when kidney function is impaired.',
      ventilatedIcu:
        'Only for ventilated patients in intensive care units. In these cases, capture is continuous and at very high frequency. A very specific parameter.',
      temperatureRoutine:
        'Regular temperature checks across all stations. More frequent with febrile infections or after surgery.',
      sepsisMarker:
        'Important sepsis and shock marker. Frequent measurements in critically ill patients, less often on general wards.',
      noUsageData:
        'Newly added parameter; no usage data available yet.',
    },
    similarityReasonLabels: {
      sameMeasurementFrequency: 'Same measurement frequency',
      similarStationUse: 'Similar station usage',
      continuousCapture: 'Continuous capture',
      vitalSign: 'Vital sign',
      relatedMeasurement: 'Related measurement',
      regularCapture: 'Regular capture',
      identicalStationUse: 'Identical station usage',
      identicalIcuStations: 'Identical ICU stations',
      sameFrequency: 'Same frequency',
      identicalMonitoringSystem: 'Identical monitoring system',
      icuVitalSign: 'ICU vital sign',
      icuStandard: 'ICU standard',
      imcVitalSign: 'IMC vital sign',
      imcStandard: 'IMC standard',
      identicalImcStations: 'Identical IMC stations',
      basicVitalSign: 'Basic vital sign',
      stationBreadth: 'Station breadth',
      generalWardRoutine: 'General-ward routine',
      intraOpStandard: 'Intraoperative standard',
      identicalOperatingRooms: 'Identical operating rooms',
      anesthesiaRequired: 'Anaesthesia-required',
      labValue: 'Lab value',
      similarSamplingFrequency: 'Similar sampling frequency',
      electrolytePanel: 'Electrolyte panel',
    },
    stationGroupLabels: {
      icu: 'Intensive care units',
      imc: 'IMC (Intermediate Care)',
      ward: 'General wards',
      opDiagnostics: 'OR & Diagnostics',
      lab: 'Lab',
      other: 'Other',
    },
    radar: {
      caseCoverage: 'Case coverage',
      completeness: 'Completeness',
      availability: 'Availability',
      stationBreadth: 'Station breadth',
      continuity: 'Continuity',
      caseCoverageDescription: 'Percentage of cases with at least one measurement',
      completenessDescription: 'Share of complete measurements without missing values',
      availabilityDescription: 'Available for {{years}} years (max. 10 years = 100%)',
      stationBreadthDescription: 'Available in {{count}} station groups (6+ = 100%)',
      continuityDescription: 'Percentage of cases with ≥10 measurements (continuous capture)',
    },
  },
  pt: {
    heroTitle: 'Disponibilidade e uso',
    cases: 'Casos',
    coverage: 'Cobertura',
    stationGroups: 'Grupos de estação',
    availableSince: 'Disponível desde',
    qualityProfile: 'Perfil de qualidade',
    quality: 'Qualidade',
    activeFilter: 'Filtro ativo:',
    followingViewsFiltered: 'Todas as visualizações seguintes estão filtradas para esta área',
    filterEnabled: 'Filtro ativado!',
    filterEnabledBody:
      'As secções Características de uso, Qualidade dos dados e Parâmetros semelhantes agora mostram dados apenas para {{group}}. Clique no ícone × no chip do filtro para voltar à visão geral.',
    availabilityByArea: 'Filtro de dados: disponibilidade por área',
    privacyTooltip: 'Estações individuais só são mostradas quando existem pelo menos 30 casos (proteção de dados)',
    selectAreaPrompt:
      'Selecione uma área para filtrar todas as visualizações seguintes (características de uso, qualidade dos dados, parâmetros semelhantes) para essa área.',
    area: 'Área',
    stations: 'Estações',
    details: 'Detalhes',
    allAreas: 'Todas as áreas',
    noStationsMeetMinimum: 'Nenhuma estação cumpre o mínimo de 30 casos (proteção de dados)',
    stationsInGroup: 'Estações em {{group}}',
    usageCharacteristics: 'Características de uso',
    basedOnGroup: 'Os dados são baseados em: {{group}}',
    basedOnAll: 'Os dados são baseados em todas as áreas (agregado)',
    measurementFrequency: 'Frequência de medição',
    mostCommon: 'Mais comum',
    median: 'Mediana',
    spread: 'Dispersão',
    intervalDistribution: 'Distribuição dos intervalos de medição',
    noData: 'Nenhum dado disponível',
    measurementsPerCase: 'Medições por caso',
    statsBasedOnCases: 'Estatísticas baseadas em {{count}} casos com pelo menos uma medição',
    averageShort: 'Média',
    total: 'Total',
    countDistribution: 'Distribuição por quantidade',
    continuousInfo: '{{percent}}% dos casos têm ≥10 medições (contínuas)',
    methodology:
      'Metodologia: Todas as estatísticas são baseadas em casos com pelo menos uma medição ({{count}} casos). As estatísticas de frequência de medição referem-se a {{group}}.',
    mainUsageArea: 'as principais áreas de uso',
    dataQuality: 'Qualidade dos dados e validação',
    completeness: 'Completude',
    plausibility: 'Plausibilidade',
    missing: 'Em falta',
    outside: '{{percent}}% fora do intervalo',
    unitsInData: 'Unidades nos dados:',
    conversionRequired: 'Conversão necessária: kPa→mmHg (×7.5), cmH₂O→mmHg (÷1.36)',
    outlierHandling: 'Tratamento de outliers',
    important: 'Importante:',
    outlierNotice: 'Valores extremos NÃO são removidos – clinicamente relevantes em sépsis, choque e crises hipertensivas.',
    iqrMethod: 'Método IQR',
    physiological: 'Fisiológico',
    systemHistory: 'Histórico do sistema',
    systemChange:
      'Mudança de sistema em 01/2020: o sistema antigo arredondava em passos de 5. O aumento da variância nas análises de 2018-2022 é um artefacto de medição, não uma mudança clínica.',
    similarParameters: 'Parâmetros com recolha semelhante',
    similarParametersTooltip: 'Parâmetros com frequência de medição, uso por estação e padrão de recolha semelhantes',
    similarParametersSummary:
      'Estes parâmetros são recolhidos com frequência semelhante e em estações semelhantes (independentemente do significado médico)',
    noSimilarParameters: 'Ainda não existem parâmetros semelhantes guardados para este filtro.',
    similarityReasons: 'Motivos de semelhança',
    frequency: 'Frequência',
    similarity: 'Semelhança',
    similarityNote:
      'Nota: a semelhança refere-se às características de recolha (frequência de medição, uso por estação, padrão de recolha), não ao significado médico.',
    measurementCountCategories: {
      single: 'Medição única (1)',
      few: 'Poucas (2-10)',
      medium: 'Médias (11-50)',
      many: 'Muitas (>50)',
    },
    similarFrequencyLabels: {
      hourly: 'De hora em hora',
      continuous: 'Contínua',
      fourHourly: 'A cada 4 horas',
      twelveToTwentyFour: '12-24h',
      irregular: 'Irregular',
      continuousBeatmet: 'Contínua (ventilados)',
      daily: 'Diária',
      everyTwoToThreeDays: 'A cada 2-3 dias',
      everyFourToSixHours: 'A cada 4-6 horas',
      asNeeded: 'Conforme necessário',
      unknown: 'Desconhecido',
    },
    measurementPatternInterpretations: {
      mostlyHourly:
        'Medições principalmente de hora em hora nas unidades de cuidados intensivos, com intervalos ocasionais de 4-12h nas enfermarias. Sinal vital muito fiável e frequentemente registado.',
      continuousIcuOr:
        'Monitorização contínua nas unidades de cuidados intensivos e no bloco operatório, com medições regulares nas enfermarias. O sinal vital registado com maior frequência.',
      continuousIcuImc:
        'Monitorização contínua na UCI e no bloco operatório, com medições regulares na IMC. Sinal vital crítico com elevada prioridade.',
      dailyLab:
        'Valores laboratoriais de rotina diários em UCI/IMC, mais frequentes quando necessário. Menos frequentes nas enfermarias.',
      renalMonitoring:
        'Monitorização regular da função renal, especialmente em doentes de risco. Mais frequente quando a função renal está comprometida.',
      ventilatedIcu:
        'Apenas para doentes ventilados nas unidades de cuidados intensivos. Nestes casos, o registo é contínuo e com frequência muito elevada. Parâmetro muito específico.',
      temperatureRoutine:
        'Controlo regular da temperatura em todas as estações. Mais frequente em infeções febris ou no pós-operatório.',
      sepsisMarker:
        'Importante marcador de sépsis e choque. Medições frequentes em doentes críticos, menos frequentes nas enfermarias.',
      noUsageData:
        'Parâmetro adicionado recentemente; ainda não existem dados de utilização.',
    },
    similarityReasonLabels: {
      sameMeasurementFrequency: 'Mesma frequência de medição',
      similarStationUse: 'Uso semelhante por estação',
      continuousCapture: 'Recolha contínua',
      vitalSign: 'Sinal vital',
      relatedMeasurement: 'Medição relacionada',
      regularCapture: 'Recolha regular',
      identicalStationUse: 'Uso idêntico por estação',
      identicalIcuStations: 'Estações ICU idênticas',
      sameFrequency: 'Mesma frequência',
      identicalMonitoringSystem: 'Sistema de monitorização idêntico',
      icuVitalSign: 'Sinal vital de ICU',
      icuStandard: 'Padrão ICU',
      imcVitalSign: 'Sinal vital de IMC',
      imcStandard: 'Padrão IMC',
      identicalImcStations: 'Estações IMC idênticas',
      basicVitalSign: 'Sinal vital básico',
      stationBreadth: 'Amplitude por estação',
      generalWardRoutine: 'Rotina de enfermaria',
      intraOpStandard: 'Padrão intraoperatório',
      identicalOperatingRooms: 'Blocos operatórios idênticos',
      anesthesiaRequired: 'Obrigatório em anestesia',
      labValue: 'Valor laboratorial',
      similarSamplingFrequency: 'Frequência de colheita semelhante',
      electrolytePanel: 'Painel de eletrólitos',
    },
    stationGroupLabels: {
      icu: 'Unidades de cuidados intensivos',
      imc: 'IMC (Cuidados Intermédios)',
      ward: 'Enfermarias',
      opDiagnostics: 'Bloco operatório e diagnóstico',
      lab: 'Laboratório',
      other: 'Outros',
    },
    radar: {
      caseCoverage: 'Cobertura de casos',
      completeness: 'Completude',
      availability: 'Disponibilidade',
      stationBreadth: 'Amplitude por estação',
      continuity: 'Continuidade',
      caseCoverageDescription: 'Percentagem de casos com pelo menos uma medição',
      completenessDescription: 'Percentagem de medições completas sem valores em falta',
      availabilityDescription: 'Disponível há {{years}} anos (máx. 10 anos = 100%)',
      stationBreadthDescription: 'Disponível em {{count}} grupos de estação (6+ = 100%)',
      continuityDescription: 'Percentagem de casos com ≥10 medições (recolha contínua)',
    },
  },
};

/**
 * DataExplorationDetail Component (Redesigned)
 * User-focused data exploration section with clear methodology
 *
 * @param {object} props
 * @param {object} props.data - Frequency and usage data
 * @param {string} props.sensitivity - Sensitivity level
 * @param {string} props.usagePattern - Usage pattern description
 * @param {string} props.parameterName - Parameter name for context
 */
const DataExplorationDetail = ({ data, sensitivity, usagePattern, parameterName = 'Parameter' }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = DATA_EXPLORATION_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';

  const {
    caseCoverage = 0,
    totalCases = 0,
    casesWithParameter = 0,
    stationCoverage = [],
    avgMeasurementsPerCase = 0,
    medianMeasurementsPerCase = 0,
    totalMeasurements = 0,
    measurementPattern = {},
    continuousCases = 0,
    singleMeasurementCases = 0,
    availableSince = 'N/A',
    similarParameters = [],
  } = data;

  // State for expandable sections
  const [expandedGroups, setExpandedGroups] = useState({});

  // State for selected station group (for filtering usage characteristics)
  const [selectedStationGroup, setSelectedStationGroup] = useState(null);

  // State for similar parameters table sorting
  const [sortBy, setSortBy] = useState('similarityScore');
  const [sortOrder, setSortOrder] = useState('desc');

  // State for loading transition
  const [isLoadingData, setIsLoadingData] = useState(false);

  // State for showing filter alert (one-time after first selection)
  const [showFilterAlert, setShowFilterAlert] = useState(false);

  const formatNumber = (value) => Number(value || 0).toLocaleString(locale);
  const normalizedSimilarParameters = Array.isArray(similarParameters)
    ? similarParameters
      .filter(item => item && typeof item === 'object')
      .map(item => ({
        id: item.id,
        name: item.name || item.localName || 'Unknown',
        description: item.description || '',
        caseCoverage: Number(item.caseCoverage || 0),
        frequency: item.frequency || copy.similarFrequencyLabels.unknown,
        stationCount: Number(item.stationCount || 0),
        similarityScore: Number(item.similarityScore || 0),
        similarityReasons: Array.isArray(item.similarityReasons)
          ? item.similarityReasons
          : (Array.isArray(item.reasons) ? item.reasons : []),
        stationGroup: item.stationGroup || '',
      }))
    : [];

  const stationGroupKeyMap = {
    'Intensivstationen': 'icu',
    'IMC (Intermediate Care)': 'imc',
    'Normalstationen': 'ward',
    'OP & Diagnostik': 'opDiagnostics',
    'Labor': 'lab',
    'Sonstige': 'other',
  };

  const translateStationGroupLabel = (name) => {
    const key = stationGroupKeyMap[name];
    return key ? copy.stationGroupLabels[key] : name;
  };

  const translateMeasurementCountCategory = (label) => {
    const mapping = {
      'Einzelmessung (1)': copy.measurementCountCategories.single,
      'Wenige (2-10)': copy.measurementCountCategories.few,
      'Mittel (11-50)': copy.measurementCountCategories.medium,
      'Viele (>50)': copy.measurementCountCategories.many,
    };

    return mapping[label] || label;
  };

  const translateSimilarFrequency = (label) => {
    const mapping = {
      'Stündlich': copy.similarFrequencyLabels.hourly,
      'Kontinuierlich': copy.similarFrequencyLabels.continuous,
      '4-stündlich': copy.similarFrequencyLabels.fourHourly,
      '12-24h': copy.similarFrequencyLabels.twelveToTwentyFour,
      'Unregelmäßig': copy.similarFrequencyLabels.irregular,
      'Kontinuierlich (beatmet)': copy.similarFrequencyLabels.continuousBeatmet,
      'Täglich': copy.similarFrequencyLabels.daily,
      'Alle 2-3 Tage': copy.similarFrequencyLabels.everyTwoToThreeDays,
      '4-6 Stunden': copy.similarFrequencyLabels.everyFourToSixHours,
      'Bei Bedarf': copy.similarFrequencyLabels.asNeeded,
      'Unbekannt': copy.similarFrequencyLabels.unknown,
    };

    return mapping[label] || label;
  };

  const translateMeasurementPatternInterpretation = (text) => {
    if (!text) return text;

    const mapping = {
      'Hauptsächlich stündliche Messungen auf ICU-Stationen, vereinzelt 4-12h Intervalle auf Normalstationen. Sehr zuverlässige und häufig erfasste Vitalparameter.':
        copy.measurementPatternInterpretations.mostlyHourly,
      'Kontinuierliche Überwachung auf Intensivstationen und OP, regelmäßige Messungen auf Normalstationen. Am häufigsten erfasster Vitalparameter.':
        copy.measurementPatternInterpretations.continuousIcuOr,
      'Kontinuierliche Überwachung auf ICU und OP, regelmäßige Messungen auf IMC. Kritischer Vitalparameter mit hoher Priorität.':
        copy.measurementPatternInterpretations.continuousIcuImc,
      'Tägliche Routine-Laborwerte auf ICU/IMC, bei Bedarf häufiger. Auf Normalstationen weniger frequent.':
        copy.measurementPatternInterpretations.dailyLab,
      'Regelmäßige Nierenfunktionskontrolle, besonders bei Risikopatienten. Häufiger bei eingeschränkter Nierenfunktion.':
        copy.measurementPatternInterpretations.renalMonitoring,
      'Nur bei beatmeten Patienten auf Intensivstationen. Bei diesen Fällen kontinuierliche Erfassung mit sehr hoher Frequenz. Sehr spezifischer Parameter.':
        copy.measurementPatternInterpretations.ventilatedIcu,
      'Regelmäßige Temperaturkontrolle auf allen Stationen. Häufiger bei fieberhaften Infektionen oder postoperativ.':
        copy.measurementPatternInterpretations.temperatureRoutine,
      'Wichtiger Sepsis- und Schock-Marker. Häufige Messungen bei kritisch kranken Patienten, seltener auf Normalstationen.':
        copy.measurementPatternInterpretations.sepsisMarker,
      'Neu hinzugefügter Parameter, noch keine Nutzungsdaten verfügbar.':
        copy.measurementPatternInterpretations.noUsageData,
    };

    return mapping[text] || text;
  };

  const translateSimilarityReason = (reason) => {
    const mapping = {
      'Gleiche Messfrequenz': copy.similarityReasonLabels.sameMeasurementFrequency,
      'Ähnliche Stationsnutzung': copy.similarityReasonLabels.similarStationUse,
      'Kontinuierliche Erfassung': copy.similarityReasonLabels.continuousCapture,
      'Vitalparameter': copy.similarityReasonLabels.vitalSign,
      'Zusammengehörige Messung': copy.similarityReasonLabels.relatedMeasurement,
      'Regelmäßige Erfassung': copy.similarityReasonLabels.regularCapture,
      'Identische Stationsnutzung': copy.similarityReasonLabels.identicalStationUse,
      'Identische ICU-Stationen': copy.similarityReasonLabels.identicalIcuStations,
      'Gleiche Frequenz': copy.similarityReasonLabels.sameFrequency,
      'Identisches Monitoring-System': copy.similarityReasonLabels.identicalMonitoringSystem,
      'ICU-Vitalparameter': copy.similarityReasonLabels.icuVitalSign,
      'ICU-Standard': copy.similarityReasonLabels.icuStandard,
      'IMC-Vitalparameter': copy.similarityReasonLabels.imcVitalSign,
      'IMC-Standard': copy.similarityReasonLabels.imcStandard,
      'Identische IMC-Stationen': copy.similarityReasonLabels.identicalImcStations,
      'Basisvitalparameter': copy.similarityReasonLabels.basicVitalSign,
      'Stationsbreite': copy.similarityReasonLabels.stationBreadth,
      'Normalstations-Routine': copy.similarityReasonLabels.generalWardRoutine,
      'Intraop-Standard': copy.similarityReasonLabels.intraOpStandard,
      'Identische OP-Säle': copy.similarityReasonLabels.identicalOperatingRooms,
      'Anästhesie-Pflicht': copy.similarityReasonLabels.anesthesiaRequired,
      'Laborwert': copy.similarityReasonLabels.labValue,
      'Ähnliche Abnahmefrequenz': copy.similarityReasonLabels.similarSamplingFrequency,
      'Elektrolyt-Panel': copy.similarityReasonLabels.electrolytePanel,
      'Ähnliche Messfrequenz': copy.similarityReasonLabels.sameMeasurementFrequency,
    };

    return mapping[reason] || reason;
  };

  // Group stations by category (based on naming convention)
  const groupStationsByType = (stations) => {
    const groups = {
      'Intensivstationen': [],
      'IMC (Intermediate Care)': [],
      'Normalstationen': [],
      'OP & Diagnostik': [],
      'Labor': [],
      'Sonstige': []
    };

    stations.forEach(station => {
      if (station.name.includes('ICU') || station.name.includes('NICU') || station.name.includes('PICU')) {
        groups['Intensivstationen'].push(station);
      } else if (station.name.includes('IMC')) {
        groups['IMC (Intermediate Care)'].push(station);
      } else if (station.name.includes('Station') || station.name.includes('Stroke Unit')) {
        groups['Normalstationen'].push(station);
      } else if (station.name.includes('OP') || station.name.includes('Radiologie') || station.name.includes('Endoskopie') || station.name.includes('Notaufnahme')) {
        groups['OP & Diagnostik'].push(station);
      } else if (station.name.includes('Labor') || station.name.includes('Blutbank')) {
        groups['Labor'].push(station);
      } else {
        groups['Sonstige'].push(station);
      }
    });

    // Remove empty groups
    return Object.entries(groups)
      .filter(([_, stations]) => stations.length > 0)
      .map(([groupName, stations]) => {
        // Calculate group statistics
        const totalGroupCases = stations.reduce((sum, s) => sum + (s.caseCount || 0), 0);
        const avgGroupCoverage = stations.reduce((sum, s) => sum + s.percentage, 0) / stations.length;

        return {
          name: groupName,
          // Only apply privacy threshold when caseCount is explicitly provided; show all stations when caseCount is absent
          stations: stations.filter(s => s.caseCount == null || (s.caseCount || 0) >= 30),
          totalCases: totalGroupCases,
          avgCoverage: avgGroupCoverage,
          stationCount: stations.length
        };
      });
  };

  const stationGroups = groupStationsByType(stationCoverage);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Handle station group selection with loading state
  const handleStationGroupChange = (groupName) => {
    setIsLoadingData(true);
    setSelectedStationGroup(groupName);

    // Show filter alert on first selection
    if (groupName && !showFilterAlert) {
      setShowFilterAlert(true);
    }

    // Simulate API call delay
    setTimeout(() => {
      setIsLoadingData(false);
    }, 300);
  };

  // Calculate continuous case percentage
  const continuousCasePercentage = casesWithParameter > 0
    ? ((continuousCases / casesWithParameter) * 100).toFixed(0)
    : 0;

  // Get filtered measurement frequency data based on selected station group
  const getFilteredMeasurementFrequency = (selectedGroup) => {
    // In production, this would call an API endpoint with the selected group
    // Mock data simulating different measurement patterns per station group

    if (!selectedGroup) {
      // All areas aggregated
      return [
        { label: '15min', percent: 5, color: '#e3f2fd' },
        { label: '1h', percent: 65, color: '#1976d2' },
        { label: '4h', percent: 20, color: '#42a5f5' },
        { label: '12h', percent: 8, color: '#90caf9' },
        { label: '24h', percent: 2, color: '#bbdefb' }
      ];
    }

    // Station-group specific patterns
    const groupPatterns = {
      'Intensivstationen': [
        { label: '15min', percent: 15, color: '#e3f2fd' },
        { label: '1h', percent: 75, color: '#1976d2' },
        { label: '4h', percent: 8, color: '#42a5f5' },
        { label: '12h', percent: 2, color: '#90caf9' },
        { label: '24h', percent: 0, color: '#bbdefb' }
      ],
      'IMC (Intermediate Care)': [
        { label: '15min', percent: 3, color: '#e3f2fd' },
        { label: '1h', percent: 45, color: '#1976d2' },
        { label: '4h', percent: 40, color: '#42a5f5' },
        { label: '12h', percent: 10, color: '#90caf9' },
        { label: '24h', percent: 2, color: '#bbdefb' }
      ],
      'Normalstationen': [
        { label: '15min', percent: 0, color: '#e3f2fd' },
        { label: '1h', percent: 10, color: '#1976d2' },
        { label: '4h', percent: 30, color: '#42a5f5' },
        { label: '12h', percent: 45, color: '#90caf9' },
        { label: '24h', percent: 15, color: '#bbdefb' }
      ],
      'OP & Diagnostik': [
        { label: '15min', percent: 25, color: '#e3f2fd' },
        { label: '1h', percent: 50, color: '#1976d2' },
        { label: '4h', percent: 20, color: '#42a5f5' },
        { label: '12h', percent: 5, color: '#90caf9' },
        { label: '24h', percent: 0, color: '#bbdefb' }
      ],
      'Labor': [
        { label: '15min', percent: 0, color: '#e3f2fd' },
        { label: '1h', percent: 5, color: '#1976d2' },
        { label: '4h', percent: 15, color: '#42a5f5' },
        { label: '12h', percent: 60, color: '#90caf9' },
        { label: '24h', percent: 20, color: '#bbdefb' }
      ]
    };

    return groupPatterns[selectedGroup] || groupPatterns['Intensivstationen'];
  };

  // Get filtered measurements per case distribution based on selected station group
  const getFilteredMeasurementsPerCase = (selectedGroup) => {
    // In production, this would call an API endpoint with the selected group

    if (!selectedGroup) {
      // All areas aggregated
      return [
        { label: 'Einzelmessung (1)', percent: 15, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 20, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 45, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 20, color: '#42a5f5' }
      ];
    }

    const groupPatterns = {
      'Intensivstationen': [
        { label: 'Einzelmessung (1)', percent: 5, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 10, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 35, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 50, color: '#42a5f5' }
      ],
      'IMC (Intermediate Care)': [
        { label: 'Einzelmessung (1)', percent: 10, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 25, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 50, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 15, color: '#42a5f5' }
      ],
      'Normalstationen': [
        { label: 'Einzelmessung (1)', percent: 30, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 40, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 25, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 5, color: '#42a5f5' }
      ],
      'OP & Diagnostik': [
        { label: 'Einzelmessung (1)', percent: 20, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 50, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 25, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 5, color: '#42a5f5' }
      ],
      'Labor': [
        { label: 'Einzelmessung (1)', percent: 40, color: '#ffcdd2' },
        { label: 'Wenige (2-10)', percent: 35, color: '#bbdefb' },
        { label: 'Mittel (11-50)', percent: 20, color: '#90caf9' },
        { label: 'Viele (>50)', percent: 5, color: '#42a5f5' }
      ]
    };

    return groupPatterns[selectedGroup] || groupPatterns['Intensivstationen'];
  };

  // Prepare data for radar chart (parameter quality profile)
  const getQualityProfileData = () => {
    // Calculate scores (0-100) for different quality dimensions
    const yearsAvailable = availableSince ? new Date().getFullYear() - parseInt(availableSince.split('-')[0]) : 0;
    const timeScore = Math.min(100, (yearsAvailable / 10) * 100); // 10 years = 100%

    return [
      {
        dimension: copy.radar.caseCoverage,
        value: caseCoverage,
        fullMark: 100,
        description: copy.radar.caseCoverageDescription
      },
      {
        dimension: copy.radar.completeness,
        value: 95, // From data quality section - could be dynamic
        fullMark: 100,
        description: copy.radar.completenessDescription
      },
      {
        dimension: copy.radar.availability,
        value: timeScore,
        fullMark: 100,
        description: copy.radar.availabilityDescription.replace('{{years}}', yearsAvailable)
      },
      {
        dimension: copy.radar.stationBreadth,
        value: Math.min(100, (stationGroups.length / 6) * 100), // 6+ groups = 100%
        fullMark: 100,
        description: copy.radar.stationBreadthDescription.replace('{{count}}', stationGroups.length)
      },
      {
        dimension: copy.radar.continuity,
        value: parseInt(continuousCasePercentage),
        fullMark: 100,
        description: copy.radar.continuityDescription
      },
    ];
  };

  // Custom tooltip for radar chart
  const CustomRadarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5, maxWidth: 250 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {data.dimension}
          </Typography>
          <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
            {Math.round(data.value)}%
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {data.description}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Handle sort for similar parameters table
  const handleSortRequest = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  // Sort similar parameters
  const sortSimilarParameters = (parameters) => {
    return [...parameters].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Get filtered data quality metrics based on selected station group
  const getFilteredDataQuality = (selectedGroup) => {
    // In production, this would call an API endpoint with the selected group
    // Mock data simulating different quality patterns per station group

    if (!selectedGroup) {
      // All areas aggregated
      return {
        completeness: { value: 95, missing: 5, pattern: 'MCAR' },
        plausibility: { value: 99.2, outsideRange: 0.8 },
        units: [
          { unit: 'mmHg', percent: 82, isPrimary: true },
          { unit: 'kPa', percent: 15, isPrimary: false },
          { unit: 'cmH₂O', percent: 3, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 8.2, lower: 75, upper: 180 },
          zscore: { percent: 2.1, lower: 60, upper: 200 },
          physiological: { percent: 0.8, lower: 40, upper: 260 }
        }
      };
    }

    // Station-group specific quality metrics
    const groupMetrics = {
      'Intensivstationen': {
        completeness: { value: 97, missing: 3, pattern: 'MCAR' },
        plausibility: { value: 99.5, outsideRange: 0.5 },
        units: [
          { unit: 'mmHg', percent: 95, isPrimary: true },
          { unit: 'kPa', percent: 5, isPrimary: false },
          { unit: 'cmH₂O', percent: 0, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 12.5, lower: 70, upper: 190 },
          zscore: { percent: 3.8, lower: 55, upper: 210 },
          physiological: { percent: 1.2, lower: 40, upper: 260 }
        }
      },
      'IMC (Intermediate Care)': {
        completeness: { value: 94, missing: 6, pattern: 'MCAR' },
        plausibility: { value: 99.0, outsideRange: 1.0 },
        units: [
          { unit: 'mmHg', percent: 88, isPrimary: true },
          { unit: 'kPa', percent: 12, isPrimary: false },
          { unit: 'cmH₂O', percent: 0, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 9.5, lower: 72, upper: 185 },
          zscore: { percent: 2.5, lower: 58, upper: 205 },
          physiological: { percent: 0.9, lower: 40, upper: 260 }
        }
      },
      'Normalstationen': {
        completeness: { value: 88, missing: 12, pattern: 'MCAR' },
        plausibility: { value: 98.5, outsideRange: 1.5 },
        units: [
          { unit: 'mmHg', percent: 65, isPrimary: true },
          { unit: 'kPa', percent: 30, isPrimary: false },
          { unit: 'cmH₂O', percent: 5, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 6.8, lower: 78, upper: 175 },
          zscore: { percent: 1.5, lower: 65, upper: 195 },
          physiological: { percent: 0.4, lower: 40, upper: 260 }
        }
      },
      'OP & Diagnostik': {
        completeness: { value: 96, missing: 4, pattern: 'MCAR' },
        plausibility: { value: 99.3, outsideRange: 0.7 },
        units: [
          { unit: 'mmHg', percent: 90, isPrimary: true },
          { unit: 'kPa', percent: 8, isPrimary: false },
          { unit: 'cmH₂O', percent: 2, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 10.2, lower: 68, upper: 195 },
          zscore: { percent: 3.1, lower: 52, upper: 215 },
          physiological: { percent: 1.0, lower: 40, upper: 260 }
        }
      },
      'Labor': {
        completeness: { value: 92, missing: 8, pattern: 'MAR' },
        plausibility: { value: 98.8, outsideRange: 1.2 },
        units: [
          { unit: 'mmHg', percent: 70, isPrimary: true },
          { unit: 'kPa', percent: 25, isPrimary: false },
          { unit: 'cmH₂O', percent: 5, isPrimary: false }
        ],
        outliers: {
          iqr: { percent: 7.5, lower: 76, upper: 178 },
          zscore: { percent: 1.8, lower: 62, upper: 198 },
          physiological: { percent: 0.6, lower: 40, upper: 260 }
        }
      }
    };

    return groupMetrics[selectedGroup] || groupMetrics['Intensivstationen'];
  };

  // Get filtered similar parameters based on selected station group
  const getFilteredSimilarParameters = (selectedGroup) => {
    return selectedGroup
      ? normalizedSimilarParameters.filter(
          item => !item.stationGroup || item.stationGroup === selectedGroup
        )
      : normalizedSimilarParameters;
  };

  const filteredSimilarParameters = sortSimilarParameters(
    getFilteredSimilarParameters(selectedStationGroup)
  );

  return (
    <Box>
      {/* Hero Section: Key Metrics */}
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 1,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {copy.heroTitle}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 0.5, alignItems: 'center' }}>
          {/* Left side: Metrics in 2x2 Grid */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
              {/* First Row */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.cases}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {formatNumber(casesWithParameter)}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.coverage}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {caseCoverage}%
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Box>

              {/* Second Row */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.stationGroups}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {stationGroups.length}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.availableSince}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {availableSince}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right side: Radar Chart */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              height: { xs: 220, md: 250 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.95, mb: 1, fontWeight: 600 }}>
                {copy.qualityProfile}
              </Typography>
              <Box sx={{ width: '100%', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={getQualityProfileData()} outerRadius="65%">
                    <PolarGrid stroke="rgba(255,255,255,0.3)" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={{ fill: 'white', fontSize: 12 }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fill: 'white', fontSize: 9 }}
                      tickCount={6}
                    />
                    <RechartsTooltip content={<CustomRadarTooltip />} />
                    <Radar
                      name={copy.quality}
                      dataKey="value"
                      stroke="#ffffff"
                      strokeWidth={2.5}
                      fill="rgba(255,255,255,0.6)"
                      fillOpacity={0.5}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Sticky Filter Bar (when filter is active) */}
      {selectedStationGroup && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 64, // Adjust based on your header height
            zIndex: 100,
            p: 1.5,
            mb: 2,
            backgroundColor: '#e3f2fd',
            borderLeft: '4px solid #1976d2',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterAltIcon color="primary" />
            <Typography variant="body2" fontWeight="bold" color="primary">
              {copy.activeFilter}
            </Typography>
            <Chip
              label={translateStationGroupLabel(selectedStationGroup)}
              color="primary"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          <Typography variant="caption" color="textSecondary">
            {copy.followingViewsFiltered}
          </Typography>
        </Paper>
      )}

      {/* Einmal-Alert nach erster Auswahl */}
      {showFilterAlert && selectedStationGroup && (
        <Fade in={showFilterAlert} timeout={500}>
          <Alert
            severity="info"
            onClose={() => setShowFilterAlert(false)}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {copy.filterEnabled}
            </Typography>
            <Typography variant="caption">
              {copy.filterEnabledBody.replace('{{group}}', translateStationGroupLabel(selectedStationGroup))}
            </Typography>
          </Alert>
        </Fade>
      )}

      {/* Section 1: Verfügbarkeit nach Bereich */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TuneIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            {copy.availabilityByArea}
          </Typography>
          <Tooltip title={copy.privacyTooltip}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Alert severity="info" icon={<TuneIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{copy.selectAreaPrompt}</strong>
          </Typography>
        </Alert>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" width="50px"></TableCell>
                <TableCell>{copy.area}</TableCell>
                <TableCell align="center">{copy.stations}</TableCell>
                <TableCell align="center">{copy.cases}</TableCell>
                <TableCell>{copy.coverage}</TableCell>
                <TableCell align="center" width="60px">{copy.details}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* "Alle Bereiche" row */}
              <TableRow
                hover
                sx={{
                  cursor: 'pointer',
                  backgroundColor: selectedStationGroup === null ? '#e3f2fd' : 'inherit',
                  '&:hover': {
                    backgroundColor: selectedStationGroup === null ? '#e3f2fd !important' : undefined
                  }
                }}
                onClick={() => handleStationGroupChange(null)}
              >
                <TableCell padding="checkbox">
                  <Radio
                    checked={selectedStationGroup === null}
                    onChange={() => handleStationGroupChange(null)}
                    value="all"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {copy.allAreas}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={stationGroups.reduce((sum, g) => sum + g.stationCount, 0)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {formatNumber(casesWithParameter || stationGroups.reduce((sum, g) => sum + g.totalCases, 0))}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={caseCoverage}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: caseCoverage >= 80 ? '#4caf50' : caseCoverage >= 50 ? '#ff9800' : '#f44336'
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 45 }}>
                      {Math.round(caseCoverage)}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" color="textSecondary">-</Typography>
                </TableCell>
              </TableRow>

              {/* Station groups */}
              {stationGroups.map((group, idx) => (
                <React.Fragment key={idx}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedStationGroup === group.name ? '#e3f2fd' : 'inherit',
                      '&:hover': {
                        backgroundColor: selectedStationGroup === group.name ? '#e3f2fd !important' : undefined
                      }
                    }}
                    onClick={() => handleStationGroupChange(group.name)}
                  >
                    <TableCell padding="checkbox">
                      <Radio
                        checked={selectedStationGroup === group.name}
                        onChange={() => handleStationGroupChange(group.name)}
                        value={group.name}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {translateStationGroupLabel(group.name)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={group.stationCount}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatNumber(group.totalCases)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={group.avgCoverage}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 1,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: group.avgCoverage >= 80 ? '#4caf50' : group.avgCoverage >= 50 ? '#ff9800' : '#f44336'
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                          {Math.round(group.avgCoverage)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroup(group.name);
                        }}
                      >
                        {expandedGroups[group.name] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expandable station details row */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedGroups[group.name] ? undefined : 'none' }}>
                      <Collapse in={expandedGroups[group.name]}>
                        <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                          {group.stations.length > 0 ? (
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                {copy.stationsInGroup.replace('{{group}}', translateStationGroupLabel(group.name))}
                              </Typography>
                              {group.stations.map((station, sIdx) => (
                                <Box key={sIdx} sx={{ mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2">
                                      {station.name}
                                    </Typography>
                                    <Chip
                                      label={`${station.percentage}%`}
                                      size="small"
                                      sx={{
                                        backgroundColor: station.color || '#1976d2',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={station.percentage}
                                      sx={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 1,
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: station.color || '#1976d2'
                                        }
                                      }}
                                    />
                                    {station.caseCount && (
                                      <Typography variant="caption" color="textSecondary" sx={{ minWidth: 80 }}>
                                      {formatNumber(station.caseCount)} {copy.cases}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                              {copy.noStationsMeetMinimum}
                            </Alert>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Section 2: Nutzungs-Charakteristik */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          position: 'relative',
          ...(selectedStationGroup && {
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#fafbff'
          })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <BarChartIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {copy.usageCharacteristics}
          </Typography>
          {selectedStationGroup && (
            <Chip
              label={translateStationGroupLabel(selectedStationGroup)}
              color="primary"
              size="small"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ ml: 'auto' }}
            />
          )}
          {isLoadingData && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>

        <Typography variant="body2" color="textSecondary" paragraph>
          {selectedStationGroup
            ? copy.basedOnGroup.replace('{{group}}', translateStationGroupLabel(selectedStationGroup))
            : copy.basedOnAll}
        </Typography>

        <Fade in={!isLoadingData} timeout={300}>
          <Grid container spacing={3}>
          {/* Messfrequenz */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TimelineIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {copy.measurementFrequency}
                </Typography>
              </Box>

              {measurementPattern.typical ? (
                <Box>
                  {/* Key Stats in compact cards */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {copy.mostCommon}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {translateSimilarFrequency(measurementPattern.typical)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {copy.median}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {measurementPattern.avgInterval || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {copy.spread}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {measurementPattern.stdDev || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Visual distribution */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                      {copy.intervalDistribution}
                    </Typography>
                    {getFilteredMeasurementFrequency(selectedStationGroup).map((interval, idx) => (
                      <Box key={idx} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" sx={{ minWidth: 45 }}>
                            {interval.label}
                          </Typography>
                          <Box sx={{ flex: 1, mx: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={interval.percent}
                              sx={{
                                height: 8,
                                borderRadius: 1,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: interval.color,
                                  borderRadius: 1
                                }
                              }}
                            />
                          </Box>
                          <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 35, textAlign: 'right' }}>
                            {interval.percent}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {measurementPattern.interpretation && (
                    <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
                      {translateMeasurementPatternInterpretation(measurementPattern.interpretation)}
                    </Alert>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {copy.noData}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Messungen pro Fall */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BarChartIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {copy.measurementsPerCase}
                </Typography>
                <Tooltip title={copy.statsBasedOnCases.replace('{{count}}', formatNumber(casesWithParameter))}>
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Key Stats */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      {copy.median}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {medianMeasurementsPerCase}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      {copy.averageShort}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {avgMeasurementsPerCase}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      {copy.total}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {(totalMeasurements / 1000000).toFixed(1)}M
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Categorized distribution */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                  {copy.countDistribution}
                </Typography>
                {getFilteredMeasurementsPerCase(selectedStationGroup).map((category, idx) => {
                  const count = Math.round(casesWithParameter * (category.percent / 100));
                  return (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography variant="caption" sx={{ minWidth: 110 }}>
                        {translateMeasurementCountCategory(category.label)}
                      </Typography>
                      <Box sx={{ flex: 1, mx: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={category.percent}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: category.color,
                              borderRadius: 1
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 50, textAlign: 'right' }}>
                        {category.percent}% ({formatNumber(count)})
                      </Typography>
                    </Box>
                  </Box>
                  );
                })}
              </Box>

              <Alert severity="info" sx={{ fontSize: '0.75rem', py: 0.5 }}>
                {copy.continuousInfo.replace('{{percent}}', continuousCasePercentage)}
              </Alert>
            </Box>
          </Grid>
        </Grid>
        </Fade>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          <Typography variant="caption">
            {copy.methodology
              .replace('{{count}}', formatNumber(casesWithParameter))
              .replace('{{group}}', translateStationGroupLabel(stationGroups[0]?.name || copy.mainUsageArea))}
          </Typography>
        </Alert>
      </Paper>

      {/* Section 3: Datenqualität */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          position: 'relative',
          ...(selectedStationGroup && {
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#fafbff'
          })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" fontWeight="bold">
            {copy.dataQuality}
          </Typography>
          {selectedStationGroup && (
            <Chip
              label={translateStationGroupLabel(selectedStationGroup)}
              color="primary"
              size="small"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ ml: 'auto' }}
            />
          )}
          {isLoadingData && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>

        <Fade in={!isLoadingData} timeout={300}>
          <Box>
            {/* Vollständigkeit & Plausibilität - Kompakter */}
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" fontSize="0.875rem">
                      {copy.completeness}
                    </Typography>
                    <Chip
                      label={`${getFilteredDataQuality(selectedStationGroup).completeness.value}%`}
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getFilteredDataQuality(selectedStationGroup).completeness.value}
                    sx={{
                      mb: 0.5,
                      height: 6,
                      borderRadius: 1,
                      '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${getFilteredDataQuality(selectedStationGroup).completeness.missing}% ${copy.missing} (${getFilteredDataQuality(selectedStationGroup).completeness.pattern})`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" fontSize="0.875rem">
                      {copy.plausibility}
                    </Typography>
                    <Chip
                      label={`${getFilteredDataQuality(selectedStationGroup).plausibility.value}%`}
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getFilteredDataQuality(selectedStationGroup).plausibility.value}
                    sx={{
                      mb: 0.5,
                      height: 6,
                      borderRadius: 1,
                      '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' }
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label="90-140 mmHg" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    <Chip
                      label={copy.outside.replace('{{percent}}', getFilteredDataQuality(selectedStationGroup).plausibility.outsideRange)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Einheiten - Verteilung in den Rohdaten */}
            <Box sx={{ mb: 2.5, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight="bold">{copy.unitsInData}</Typography>
                {getFilteredDataQuality(selectedStationGroup).units.map((unitData, idx) => (
                  <Chip
                    key={idx}
                    label={`${unitData.unit} (${unitData.percent}%)`}
                    size="small"
                    color={unitData.isPrimary ? "primary" : "default"}
                    variant={unitData.isPrimary ? "filled" : "outlined"}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: unitData.isPrimary ? 'bold' : 'normal',
                      ...(unitData.isPrimary ? {} : { borderColor: '#1976d2', color: '#1976d2' })
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                {copy.conversionRequired}
              </Typography>
            </Box>

            {/* Outlier - Kompakter */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
                <WarningAmberIcon fontSize="small" color="warning" />
                {copy.outlierHandling}
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fffbf0' }}>
                <Alert severity="info" icon={false} sx={{ mb: 1.5, fontSize: '0.7rem', py: 0.5, backgroundColor: 'transparent' }}>
                  <strong>{copy.important}</strong> {copy.outlierNotice}
                </Alert>

                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary" display="block">{copy.iqrMethod}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {getFilteredDataQuality(selectedStationGroup).outliers.iqr.percent}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                        {'<'}{getFilteredDataQuality(selectedStationGroup).outliers.iqr.lower} | {'>'}{getFilteredDataQuality(selectedStationGroup).outliers.iqr.upper} mmHg
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary" display="block">Z-Score</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {getFilteredDataQuality(selectedStationGroup).outliers.zscore.percent}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                        {'<'}{getFilteredDataQuality(selectedStationGroup).outliers.zscore.lower} | {'>'}{getFilteredDataQuality(selectedStationGroup).outliers.zscore.upper} mmHg
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary" display="block">{copy.physiological}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {getFilteredDataQuality(selectedStationGroup).outliers.physiological.percent}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                        {'<'}{getFilteredDataQuality(selectedStationGroup).outliers.physiological.lower} | {'>'}{getFilteredDataQuality(selectedStationGroup).outliers.physiological.upper} mmHg
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Box>
        </Fade>

        {/* Zeitliche Konsistenz - Kompakte Timeline */}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
            <InfoOutlinedIcon fontSize="small" color="primary" />
            {copy.systemHistory}
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="caption" fontWeight="bold">2020+:</Typography>
                <Typography variant="caption" color="textSecondary">Philips IntelliVue</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmberIcon color="warning" fontSize="small" />
                <Typography variant="caption" fontWeight="bold">{'<2020:'}</Typography>
                <Typography variant="caption" color="textSecondary">GE Datex (5er-Rundung)</Typography>
              </Box>
            </Box>
            <Alert severity="warning" sx={{ fontSize: '0.7rem', py: 0.5 }}>
              {copy.systemChange}
            </Alert>
          </Paper>
        </Box>
      </Paper>

      {/* Section 4: Parameter mit ähnlicher Erfassung */}
      <Paper
        sx={{
          p: 3,
          position: 'relative',
          ...(selectedStationGroup && {
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#fafbff'
          })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <SearchIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {copy.similarParameters}
          </Typography>
          <Tooltip title={copy.similarParametersTooltip}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {selectedStationGroup && (
            <Chip
              label={translateStationGroupLabel(selectedStationGroup)}
              color="primary"
              size="small"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ ml: 'auto' }}
            />
          )}
          {isLoadingData && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>

        <Typography variant="body2" color="textSecondary" paragraph>
          {copy.similarParametersSummary}
        </Typography>

        <Fade in={!isLoadingData} timeout={300}>
          <Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'name'}
                        direction={sortBy === 'name' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('name')}
                      >
                        Parameter
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{copy.similarityReasons}</TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'caseCoverage'}
                        direction={sortBy === 'caseCoverage' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('caseCoverage')}
                      >
                        {copy.coverage}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'frequency'}
                        direction={sortBy === 'frequency' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('frequency')}
                      >
                        {copy.frequency}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'stationCount'}
                        direction={sortBy === 'stationCount' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('stationCount')}
                      >
                        {copy.stations}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'similarityScore'}
                        direction={sortBy === 'similarityScore' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('similarityScore')}
                      >
                        {copy.similarity}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSimilarParameters.length > 0 ? filteredSimilarParameters.map((param, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  onClick={() => navigate(`/parameter/${getParameterLookupId(param)}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {param.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {param.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {param.similarityReasons.map((reason, rIdx) => (
                        <Chip
                          key={rIdx}
                          label={translateSimilarityReason(reason)}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${param.caseCoverage}%`}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                      <TimelineIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {translateSimilarFrequency(param.frequency)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                      <GroupIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {param.stationCount}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        backgroundColor: param.similarityScore >= 80 ? '#e8f5e9' : param.similarityScore >= 60 ? '#fff3e0' : '#f5f5f5',
                        border: `2px solid ${param.similarityScore >= 80 ? '#4caf50' : param.similarityScore >= 60 ? '#ff9800' : '#bdbdbd'}`
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={param.similarityScore >= 80 ? 'success.main' : param.similarityScore >= 60 ? 'warning.main' : 'textSecondary'}
                      >
                        {param.similarityScore}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="info">{copy.noSimilarParameters}</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          <Typography variant="caption">
            {copy.similarityNote}
          </Typography>
        </Alert>
          </Box>
        </Fade>
      </Paper>
    </Box>
  );
};

export default DataExplorationDetail;
